import React, { useState, useMemo } from 'react';
import { Droppable, Draggable, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Inbox, Calendar, User, AlertTriangle, ChevronDown, ChevronRight, Wrench, Zap, Check, X, Users } from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';

import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';

interface Tecnico {
  user_id: string;
  nombre: string;
}

interface BandejaOTsProps {
  ordenes: OrdenTrabajo[];
  tecnicos?: Tecnico[];
  onOrdenClick: (orden: OrdenTrabajo) => void;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
}

const prioridadColors: Record<string, string> = {
  urgente: 'bg-red-500/20 text-red-400 border-red-500/30',
  alta: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  media: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  baja: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const prioridadLabels: Record<string, string> = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

const tipoTrabajoLabels: Record<string, string> = {
  mantenimiento: 'M',
  revision: 'R',
  instalacion: 'I',
  puesta_en_marcha: 'PM',
  auditoria: 'A',
  reparacion: 'Av',
};

const prioridadOrder: Record<string, number> = {
  urgente: 0,
  alta: 1,
  media: 2,
  baja: 3,
};

export const BandejaOTs: React.FC<BandejaOTsProps> = ({ 
  ordenes, 
  tecnicos = [], 
  onOrdenClick,
  selectedIds,
  onSelectionChange 
}) => {
  const { assignMultipleToTecnico } = useOrdenesTrabajoContext();
  const [selectedTecnico, setSelectedTecnico] = useState<string>('');
  const [mantenimientosOpen, setMantenimientosOpen] = useState(true);
  const [otrosOpen, setOtrosOpen] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);

  // Filter and sort unassigned orders
  const ordenesEnBandeja = useMemo(() => 
    ordenes
      .filter(o => !o.tecnico_asignado_id && !['finalizada', 'cancelada'].includes(o.estado))
      .sort((a, b) => {
        const prioridadDiff = prioridadOrder[a.prioridad] - prioridadOrder[b.prioridad];
        if (prioridadDiff !== 0) return prioridadDiff;
        
        if (a.fecha_limite && b.fecha_limite) {
          return new Date(a.fecha_limite).getTime() - new Date(b.fecha_limite).getTime();
        }
        if (a.fecha_limite) return -1;
        if (b.fecha_limite) return 1;
        return 0;
      }),
    [ordenes]
  );

  // Separate into categories
  const mantenimientos = useMemo(() => 
    ordenesEnBandeja.filter(o => ['mantenimiento', 'revision'].includes(o.tipo_trabajo || '')),
    [ordenesEnBandeja]
  );

  const otrosTrabajos = useMemo(() => 
    ordenesEnBandeja.filter(o => !['mantenimiento', 'revision'].includes(o.tipo_trabajo || '')),
    [ordenesEnBandeja]
  );

  const isSelectionMode = selectedIds.size > 0;

  const getFechaLimiteStatus = (fechaLimite?: string | null) => {
    if (!fechaLimite) return null;
    const fecha = new Date(fechaLimite);
    if (isPast(fecha) && !isToday(fecha)) {
      return { text: 'Vencida', className: 'text-red-400' };
    }
    if (isToday(fecha)) {
      return { text: 'Hoy', className: 'text-yellow-400' };
    }
    return null;
  };

  const toggleSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    onSelectionChange(newSet);
  };

  const handleSelectAll = (items: OrdenTrabajo[], e: React.MouseEvent) => {
    e.stopPropagation();
    const allSelected = items.every(item => selectedIds.has(item.id));
    const newSet = new Set(selectedIds);
    items.forEach(item => {
      if (allSelected) {
        newSet.delete(item.id);
      } else {
        newSet.add(item.id);
      }
    });
    onSelectionChange(newSet);
  };

  const handleCancelSelection = () => {
    onSelectionChange(new Set());
    setSelectedTecnico('');
  };

  const handleMassAssign = async () => {
    if (!selectedTecnico || selectedIds.size === 0) return;
    
    setIsAssigning(true);
    try {
      await assignMultipleToTecnico(Array.from(selectedIds), selectedTecnico);
      onSelectionChange(new Set());
      setSelectedTecnico('');
    } finally {
      setIsAssigning(false);
    }
  };

  // Render card content - used both in Draggable and renderClone
  const renderOrdenCardContent = (
    orden: OrdenTrabajo, 
    provided: DraggableProvided, 
    snapshot: DraggableStateSnapshot,
    isClone: boolean = false
  ) => {
    const fechaStatus = getFechaLimiteStatus(orden.fecha_limite);
    const isUrgent = orden.prioridad === 'urgente';
    const isSelected = selectedIds.has(orden.id);
    const tipoLabel = tipoTrabajoLabels[orden.tipo_trabajo || 'reparacion'] || 'Av';
    
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={`mb-2 rounded-lg border bg-card p-3 cursor-grab active:cursor-grabbing select-none transition-shadow ${
          isUrgent ? 'border-red-500/50 bg-red-500/5' : 'border-border'
        } ${
          isSelected ? 'ring-2 ring-primary border-primary' : ''
        } ${
          snapshot.isDragging 
            ? 'shadow-2xl ring-2 ring-amber-500' 
            : 'hover:shadow-md hover:border-amber-500/30'
        }`}
        style={{
          ...provided.draggableProps.style,
          // Force proper dimensions when cloned
          ...(isClone ? { width: '260px' } : {}),
        }}
        onClick={() => !isSelectionMode && !snapshot.isDragging ? onOrdenClick(orden) : undefined}
      >
        <div className="flex items-center gap-2 mb-2">
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              onClick={(e) => toggleSelection(orden.id, e as unknown as React.MouseEvent)}
              className="data-[state=checked]:bg-primary"
            />
          </div>
          <Badge variant="outline" className="text-xs font-mono bg-muted">
            {tipoLabel}
          </Badge>
          <span className="text-xs font-mono text-muted-foreground flex-1">
            {orden.numero_ot}
          </span>
          <Badge 
            variant="outline" 
            className={`text-xs ${prioridadColors[orden.prioridad]}`}
          >
            {prioridadLabels[orden.prioridad]}
          </Badge>
        </div>
        
        <h4 className="font-medium text-sm mb-2 line-clamp-2">
          {orden.titulo}
        </h4>
        
        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
          <User className="h-3 w-3" />
          {orden.cliente_nombre || 'Sin cliente'}
        </p>
        
        {orden.fecha_limite && (
          <div className={`flex items-center gap-1 text-xs ${
            fechaStatus?.className || 'text-muted-foreground'
          }`}>
            {fechaStatus && <AlertTriangle className="h-3 w-3" />}
            <Calendar className="h-3 w-3" />
            <span>
              {fechaStatus?.text || format(new Date(orden.fecha_limite), 'dd MMM', { locale: es })}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderOrdenCard = (orden: OrdenTrabajo, index: number) => {
    return (
      <Draggable key={orden.id} draggableId={orden.id} index={index}>
        {(provided, snapshot) => renderOrdenCardContent(orden, provided, snapshot, false)}
      </Draggable>
    );
  };

  const renderSectionHeader = (
    title: string, 
    icon: React.ReactNode, 
    items: OrdenTrabajo[], 
    isOpen: boolean, 
    setIsOpen: (open: boolean) => void
  ) => {
    const allSelected = items.length > 0 && items.every(item => selectedIds.has(item.id));
    const someSelected = items.some(item => selectedIds.has(item.id));
    
    return (
      <div 
        className="flex items-center justify-between w-full p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer mb-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {icon}
          <span className="text-sm font-medium">{title}</span>
          <Badge variant="secondary" className="text-xs">
            {items.length}
          </Badge>
        </div>
        {items.length > 0 && (
          <Checkbox
            checked={allSelected}
            className={`${someSelected && !allSelected ? 'data-[state=checked]:bg-primary/50' : ''}`}
            onClick={(e) => handleSelectAll(items, e as unknown as React.MouseEvent)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="flex-shrink-0 w-72">
      <Card className="bg-gradient-to-b from-amber-500/5 to-amber-500/10 border-amber-500/30 h-full">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Inbox className="h-4 w-4 text-amber-400" />
              </div>
              <CardTitle className="text-sm font-medium text-amber-400">
                Bandeja de Entrada
              </CardTitle>
            </div>
            <Badge variant="secondary" className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">
              {ordenesEnBandeja.length}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {isSelectionMode 
              ? `${selectedIds.size} seleccionadas` 
              : 'Arrastra o selecciona para asignar'}
          </p>
        </CardHeader>

        {/* Mass Assignment Bar */}
        {isSelectionMode && (
          <div className="px-4 pb-3 space-y-2 animate-fade-in">
            <Select value={selectedTecnico} onValueChange={setSelectedTecnico}>
              <SelectTrigger className="h-8 text-xs bg-background">
                <Users className="h-3 w-3 mr-2" />
                <SelectValue placeholder="Seleccionar técnico" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                {tecnicos.map((tecnico) => (
                  <SelectItem key={tecnico.user_id} value={tecnico.user_id}>
                    {tecnico.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                className="flex-1 h-8 text-xs"
                onClick={handleMassAssign}
                disabled={!selectedTecnico || isAssigning}
              >
                <Check className="h-3 w-3 mr-1" />
                {isAssigning ? 'Asignando...' : `Asignar ${selectedIds.size}`}
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="h-8"
                onClick={handleCancelSelection}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        <CardContent className="pt-0">
          <Droppable 
            droppableId="bandeja"
            renderClone={(provided, snapshot, rubric) => {
              const orden = ordenesEnBandeja.find(o => o.id === rubric.draggableId);
              if (!orden) return <div ref={provided.innerRef} {...provided.draggableProps} />;
              return renderOrdenCardContent(orden, provided, snapshot, true);
            }}
          >
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`min-h-[100px] max-h-[calc(100vh-380px)] overflow-y-auto rounded-lg transition-all duration-200 ${
                  snapshot.isDraggingOver 
                    ? 'bg-amber-500/10 ring-2 ring-amber-500/30 ring-inset' 
                    : ''
                }`}
              >
                {ordenesEnBandeja.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    <Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Sin OTs pendientes</p>
                    <p className="text-xs mt-1">Las nuevas OTs aparecerán aquí</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Mantenimientos Section */}
                    {mantenimientos.length > 0 && (
                      <>
                        {renderSectionHeader(
                          'Mantenimientos',
                          <Wrench className="h-4 w-4 text-blue-400" />,
                          mantenimientos,
                          mantenimientosOpen,
                          setMantenimientosOpen
                        )}
                        {mantenimientosOpen && (
                          <div className="pl-2 animate-accordion-down">
                            {mantenimientos.map((orden, idx) => 
                              renderOrdenCard(orden, idx)
                            )}
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Otros Trabajos Section */}
                    {otrosTrabajos.length > 0 && (
                      <>
                        {renderSectionHeader(
                          'Otros Trabajos',
                          <Zap className="h-4 w-4 text-orange-400" />,
                          otrosTrabajos,
                          otrosOpen,
                          setOtrosOpen
                        )}
                        {otrosOpen && (
                          <div className="pl-2 animate-accordion-down">
                            {otrosTrabajos.map((orden, idx) => 
                              renderOrdenCard(orden, mantenimientos.length + idx)
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </CardContent>
      </Card>
    </div>
  );
};
